import { CheckCircle2, Camera, FileText, Flag, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const META = [
    { label: "Project", value: "Residential Duplex — Gorakhpur" },
    { label: "Inspection Date", value: "12 June 2026" },
    { label: "Inspection Scope", value: "Visible progress & workmanship — Ground + First Floor" },
    { label: "Documents Reviewed", value: "BOQ v2 · Contractor Bill No. 7 · Approved Drawings" },
];

const OBSERVATIONS = [
    { text: "Brickwork alignment within tolerance at GF; pointing incomplete at FF stairwell", priority: "Medium" },
    { text: "Cover blocks missing on two FF columns — to be corrected before slab casting", priority: "High" },
    { text: "Curing record maintained; ponding observed on GF roof slab", priority: "Low" },
];

const PRIORITY_STYLES = {
    High: "bg-amber/10 text-amber border-amber/30",
    Medium: "bg-navy/5 text-navy border-navy/20",
    Low: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ReportMockup() {
    return (
        <Reveal>
            <div
                className="border border-slate-200 bg-white shadow-[0_32px_80px_-32px_rgba(10,17,40,0.35)]"
                data-testid="report-mockup"
            >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-navy px-6 py-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                            Sample Format
                        </p>
                        <p className="font-display text-base font-bold text-white">
                            Independent Construction Review Report
                        </p>
                    </div>
                    <div className="border border-amber/50 px-3 py-1.5">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Report ID</p>
                        <p className="font-mono text-sm font-semibold text-amber" data-testid="report-id">
                            SCWC-RPT-2026-0147
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2">
                    {META.map((m) => (
                        <div key={m.label} className="border-b border-slate-200 px-6 py-4 md:odd:border-r">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{m.label}</p>
                            <p className="mt-1 text-sm font-medium text-navy">{m.value}</p>
                        </div>
                    ))}
                </div>

                <div className="border-b border-slate-200 px-6 py-5">
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Key Observations
                    </p>
                    <ul className="space-y-3">
                        {OBSERVATIONS.map((o) => (
                            <li key={o.text} className="flex flex-wrap items-start justify-between gap-2">
                                <span className="flex items-start gap-2 text-sm text-slate-700">
                                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-amber" />
                                    {o.text}
                                </span>
                                <span
                                    className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] ${PRIORITY_STYLES[o.priority]}`}
                                >
                                    {o.priority}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3">
                    <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-4 sm:border-b-0 sm:border-r">
                        <Camera size={18} className="shrink-0 text-navy" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Photographic Evidence</p>
                            <p className="text-sm font-medium text-navy">14 photographs attached</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-4 sm:border-b-0 sm:border-r">
                        <Flag size={18} className="shrink-0 text-navy" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recommended Action</p>
                            <p className="text-sm font-medium text-navy">Rectify before next stage</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 px-6 py-4">
                        <FileText size={18} className="shrink-0 text-navy" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Next Steps</p>
                            <p className="text-sm font-medium text-navy">Re-inspection after rectification</p>
                        </div>
                    </div>
                </div>

                <p className="border-t border-slate-200 bg-slate-50 px-6 py-3 text-[11px] leading-relaxed text-slate-500">
                    This report is a technical review document based on the agreed scope and visible, site-accessible
                    conditions. It does not constitute a structural safety certification or a legal certification.
                </p>
            </div>
            <a
                href="/contact?service=Site%20Inspection"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-amber hover:text-amber-hover"
                data-testid="report-mockup-cta"
            >
                Request a report like this <ArrowRight size={14} />
            </a>
        </Reveal>
    );
}
